import type { ComponentProps } from "react";
import { getAvatarVisibleCount } from "@design-system/Avatar/avatarVisibleCount";
import type { UserAvatar } from "./UserAvatar";

export type PresenceStackUser = ComponentProps<typeof UserAvatar>["user"];

/**
 * Where a person sits in the stack: `pinned` (the Tasks assignee) at the
 * right end and never hidden; `team` (the Work engagement team) at the right
 * end but hidden before viewers; `viewer` (someone with the item open now).
 */
export type PresencePersonRole = "pinned" | "team" | "viewer";

export type PresenceStackMember =
    | { kind: "agent"; detail: string; isLive: boolean }
    | {
          kind: "person";
          id: string;
          /** Shown in the tooltip and the assignee button's name, e.g. `Carla C.`. */
          name: string;
          user: PresenceStackUser;
          role: PresencePersonRole;
          detail: string;
          isLive: boolean;
      }
    | { kind: "unassigned" };

export interface PresenceStackOrder {
    /** The members drawn as avatars, left to right. */
    visible: PresenceStackMember[];
    /** The members behind the +N chip, in the same order. */
    hidden: PresenceStackMember[];
}

/** Avatars before the +N chip, unless more members can never be hidden. */
export const PRESENCE_STACK_MAX = 3;

export const AGENT_NAME = "Kick";
export const NO_ASSIGNEE_NAME = "No assignee";

/** The member's name in the tooltip, the +N chip's hidden text and the assignee button's name. */
export const getPresenceMemberName = (member: PresenceStackMember) => {
    switch (member.kind) {
        case "agent":
            return AGENT_NAME;
        case "unassigned":
            return NO_ASSIGNEE_NAME;
        case "person":
            return member.name;
    }
};

/** A stable key per member; there is at most one agent and one unassigned slot. */
export const getPresenceMemberKey = (member: PresenceStackMember) =>
    member.kind === "person" ? member.id : member.kind;

/** The Tasks assignee slot: the pinned person or the unassigned placeholder. */
export const isPresenceAssignee = (member: PresenceStackMember) =>
    member.kind === "unassigned" ||
    (member.kind === "person" && member.role === "pinned");

const getRank = (member: PresenceStackMember) => {
    switch (member.kind) {
        case "agent":
            return 0;
        case "unassigned":
            return 4;
        case "person":
            if (member.role === "viewer") {
                return member.isLive ? 1 : 2;
            }
            return member.role === "team" ? 3 : 4;
    }
};

const isNeverHidden = (member: PresenceStackMember) =>
    member.kind !== "person" || member.role === "pinned";

/**
 * Orders a presence stack left to right (the agent, live viewers, idle
 * viewers, then the team and the pinned assignee at the right end) and caps
 * it at `PRESENCE_STACK_MAX` avatars. The agent, the pinned assignee and the
 * unassigned slot are never hidden; team members drop before viewers. Like
 * every `AvatarGroup`, one extra member shows instead of a "+1" chip.
 */
export const getPresenceStackOrder = (
    members: readonly PresenceStackMember[],
): PresenceStackOrder => {
    const ordered = members.toSorted(
        (first, second) => getRank(first) - getRank(second),
    );
    const keptMembers = ordered.filter(isNeverHidden);
    const visibleCount = getAvatarVisibleCount({
        itemCount: ordered.length,
        max: Math.max(PRESENCE_STACK_MAX, keptMembers.length),
    });
    const shownMembers = new Set([
        ...keptMembers,
        ...ordered
            .filter((member) => !isNeverHidden(member))
            .slice(0, visibleCount - keptMembers.length),
    ]);
    return {
        visible: ordered.filter((member) => shownMembers.has(member)),
        hidden: ordered.filter((member) => !shownMembers.has(member)),
    };
};
